import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { Modal } from "@/components/ui/Modal";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { MergeFieldPicker } from "./MergeFieldPicker";
import type { AdminGameDetail } from "@/types";

// ── GraphQL ──────────────────────────────────────────

const ADMIN_BGG_SEARCH_QUERY = gql`
  query AdminBggSearch($query: String!, $gameId: ID) {
    adminBggSearch(query: $query, gameId: $gameId) {
      bggId
      title
      yearPublished
      thumbnailUrl
      descriptionSnippet
      existingGameId
      existingGameTitle
      imageSimilarity
    }
  }
`;

const ADMIN_INTERNAL_SEARCH_QUERY = gql`
  query AdminInternalGameSearch($query: String!, $sourceGameId: ID) {
    adminInternalGameSearch(query: $query, sourceGameId: $sourceGameId) {
      id
      title
      yearPublished
      imageUrl
      thumbnailUrl
      bggId
      descriptionSnippet
      imageSimilarity
      activeListingsCount
      approvalStatus
      primaryImageUrl
    }
  }
`;

const MAP_GAME_TO_BGG_MUTATION = gql`
  mutation MapGameToBgg($gameId: ID!, $bggId: Int!) {
    mapGameToBgg(gameId: $gameId, bggId: $bggId) {
      game { id }
      merged
      errors
    }
  }
`;

const MERGE_GAMES_MUTATION = gql`
  mutation MergeGames($sourceGameId: ID!, $targetGameId: ID!, $fieldOverrides: JSON) {
    mergeGames(sourceGameId: $sourceGameId, targetGameId: $targetGameId, fieldOverrides: $fieldOverrides) {
      game { id title }
      errors
    }
  }
`;

const ADMIN_GAME_QUERY = gql`
  query AdminGame($id: ID!) {
    adminGame(id: $id) {
      id title searchableTitle yearPublished dataSource
      bggId bggNotFound bggLastSyncedAt bggRating bggRank bayesAverage usersRated
      localRating localVotesCount displayRating ratingSource
      complexity minPlayers maxPlayers minPlaytime maxPlaytime minAge
      languageIndependent isExpansion imageUrl thumbnailUrl
      descriptionEn descriptionLt designer publisher
      designersList publishersList mechanics genres categories families expansions artists
      boxLength boxWidth boxHeight approvalStatus public
      alternateNames activeListingsCount
      shopListings { id shopName listingUrl price priceUpdatedAt updatedAt }
      createdAt updatedAt
    }
  }
`;

// ── Types ────────────────────────────────────────────

interface BggSearchResult {
  bggId: number;
  title: string | null;
  yearPublished: number | null;
  thumbnailUrl: string | null;
  descriptionSnippet: string | null;
  existingGameId: string | null;
  existingGameTitle: string | null;
  imageSimilarity: number | null;
}

interface InternalSearchResult {
  id: string;
  title: string;
  yearPublished: number | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  bggId: number | null;
  descriptionSnippet: string | null;
  imageSimilarity: number | null;
  activeListingsCount: number;
  approvalStatus: string;
  primaryImageUrl: string | null;
}

type Tab = "bgg" | "internal";
type ModalStep = "search" | "field-picker";

// ── Component ───────────────────────────────────────

interface GameMergeModalProps {
  open: boolean;
  onClose: () => void;
  sourceGame: AdminGameDetail;
  initialTargetId?: string | null;
}

export function GameMergeModal({ open, onClose, sourceGame, initialTargetId }: GameMergeModalProps) {
  const [tab, setTab] = useState<Tab>("bgg");
  const [step, setStep] = useState<ModalStep>(initialTargetId ? "field-picker" : "search");
  const [searchInput, setSearchInput] = useState(sourceGame.title);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(initialTargetId ?? null);
  const [mutationErrors, setMutationErrors] = useState<string[]>([]);

  const queryClient = useQueryClient();

  // BGG search
  const { data: bggData, isLoading: bggLoading } = useQuery({
    queryKey: ["adminBggSearch", searchQuery],
    queryFn: () =>
      graphqlClient.request<{ adminBggSearch: BggSearchResult[] }>(
        ADMIN_BGG_SEARCH_QUERY,
        { query: searchQuery, gameId: sourceGame.id },
      ),
    enabled: tab === "bgg" && searchQuery.length > 0,
  });

  // Internal search
  const { data: internalData, isLoading: internalLoading } = useQuery({
    queryKey: ["adminInternalSearch", searchQuery],
    queryFn: () =>
      graphqlClient.request<{ adminInternalGameSearch: InternalSearchResult[] }>(
        ADMIN_INTERNAL_SEARCH_QUERY,
        { query: searchQuery, sourceGameId: sourceGame.id },
      ),
    enabled: tab === "internal" && searchQuery.length > 0,
  });

  // Fetch full target game for field picker
  const { data: targetGameData } = useQuery({
    queryKey: ["adminGame", selectedTargetId],
    queryFn: () =>
      graphqlClient.request<{ adminGame: AdminGameDetail }>(ADMIN_GAME_QUERY, {
        id: selectedTargetId,
      }),
    enabled: !!selectedTargetId && step === "field-picker",
  });

  // BGG map mutation (existing flow)
  const mapBggMutation = useMutation({
    mutationFn: (bggId: number) =>
      graphqlClient.request<{
        mapGameToBgg: { game: { id: string } | null; merged: boolean; errors: string[] };
      }>(MAP_GAME_TO_BGG_MUTATION, { gameId: sourceGame.id, bggId }),
    onSuccess: (data) => {
      const result = data.mapGameToBgg;
      if (result.errors.length > 0) {
        setMutationErrors(result.errors);
        return;
      }
      handleMergeSuccess();
    },
  });

  // Internal merge mutation
  const mergeMutation = useMutation({
    mutationFn: (params: { targetGameId: string; fieldOverrides?: Record<string, string> }) =>
      graphqlClient.request<{
        mergeGames: { game: { id: string; title: string } | null; errors: string[] };
      }>(MERGE_GAMES_MUTATION, {
        sourceGameId: sourceGame.id,
        targetGameId: params.targetGameId,
        fieldOverrides: params.fieldOverrides,
      }),
    onSuccess: (data) => {
      const result = data.mergeGames;
      if (result.errors.length > 0) {
        setMutationErrors(result.errors);
        return;
      }
      handleMergeSuccess();
    },
  });

  const handleMergeSuccess = () => {
    setMutationErrors([]);
    queryClient.invalidateQueries({ queryKey: ["adminGame"] });
    queryClient.invalidateQueries({ queryKey: ["adminGames"] });
    queryClient.invalidateQueries({ queryKey: ["adminMergeSuggestions"] });
    handleClose();
  };

  const handleClose = () => {
    setStep("search");
    setSelectedTargetId(null);
    setMutationErrors([]);
    onClose();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const handleInternalMerge = (targetId: string) => {
    setSelectedTargetId(targetId);
    setStep("field-picker");
  };

  const handleFieldPickerConfirm = (fieldOverrides: Record<string, string>) => {
    if (!selectedTargetId) return;
    mergeMutation.mutate({ targetGameId: selectedTargetId, fieldOverrides });
  };

  const bggResults = bggData?.adminBggSearch ?? [];
  const internalResults = internalData?.adminInternalGameSearch ?? [];
  const isSearching = tab === "bgg" ? bggLoading : internalLoading;
  const targetGame = targetGameData?.adminGame;
  const isPending = mapBggMutation.isPending || mergeMutation.isPending;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={step === "field-picker" ? "Pasirinkite reikšmes" : "Sujungti / Susieti"}
      size={step === "field-picker" ? "4xl" : "2xl"}
    >
      {step === "search" && (
        <>
          {/* Tabs */}
          <div className="mb-4 flex gap-1 rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => setTab("bgg")}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === "bgg"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              BGG paieška
            </button>
            <button
              onClick={() => setTab("internal")}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === "internal"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Vidinė paieška
            </button>
          </div>

          {/* Search form */}
          <form onSubmit={handleSearch} className="mb-4 flex gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={tab === "bgg" ? "Ieškoti BGG..." : "Ieškoti žaidimų..."}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <button
              type="submit"
              className="shrink-0 rounded-lg bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Ieškoti
            </button>
          </form>

          {/* Loading */}
          {isSearching && (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {/* Empty state */}
          {!isSearching && searchQuery && ((tab === "bgg" && bggResults.length === 0) || (tab === "internal" && internalResults.length === 0)) && (
            <p className="py-8 text-center text-sm text-gray-500">
              Rezultatų nerasta
            </p>
          )}

          {/* BGG Results */}
          {tab === "bgg" && bggResults.length > 0 && (
            <div className="max-h-[60vh] space-y-3 overflow-y-auto">
              {bggResults.map((result) => (
                <BggResultCard
                  key={result.bggId}
                  result={result}
                  onSelect={() => mapBggMutation.mutate(result.bggId)}
                  isPending={isPending}
                  onClose={handleClose}
                />
              ))}
            </div>
          )}

          {/* Internal Results */}
          {tab === "internal" && internalResults.length > 0 && (
            <div className="max-h-[60vh] space-y-3 overflow-y-auto">
              {internalResults.map((result) => (
                <InternalResultCard
                  key={result.id}
                  result={result}
                  onSelect={() => handleInternalMerge(result.id)}
                  isPending={isPending}
                />
              ))}
            </div>
          )}
        </>
      )}

      {step === "field-picker" && targetGame && (
        <MergeFieldPicker
          sourceGame={sourceGame}
          targetGame={targetGame}
          onConfirm={handleFieldPickerConfirm}
          onBack={() => {
            setStep("search");
            setSelectedTargetId(null);
          }}
          isPending={isPending}
        />
      )}

      {step === "field-picker" && !targetGame && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {/* Errors */}
      {mutationErrors.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
          {mutationErrors.map((error, i) => (
            <p key={i} className="text-sm text-red-600">{error}</p>
          ))}
        </div>
      )}

      {(mapBggMutation.isError || mergeMutation.isError) && (
        <p className="mt-3 text-sm text-red-600">
          Klaida. Bandykite dar kartą.
        </p>
      )}
    </Modal>
  );
}

// ── Sub-components ──────────────────────────────────

function SimilarityBadge({ value }: { value: number | null }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const color =
    value >= 0.85
      ? "bg-green-100 text-green-800"
      : value >= 0.7
        ? "bg-yellow-100 text-yellow-800"
        : "bg-red-100 text-red-800";
  return (
    <span className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${color}`}>
      {pct}% match
    </span>
  );
}

function GameThumb({ url, alt }: { url: string | null; alt: string }) {
  if (url) {
    return <img src={url} alt={alt} className="h-32 w-32 shrink-0 rounded-lg object-cover" />;
  }
  return (
    <div className="flex h-32 w-32 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
      <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z"
        />
      </svg>
    </div>
  );
}

function BggResultCard({
  result,
  onSelect,
  isPending,
  onClose,
}: {
  result: BggSearchResult;
  onSelect: () => void;
  isPending: boolean;
  onClose: () => void;
}) {
  return (
    <div
      className={`flex gap-4 rounded-lg border p-3 ${
        result.existingGameId ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-white"
      }`}
    >
      <GameThumb url={result.thumbnailUrl} alt={result.title ?? ""} />
      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <div>
          <div className="flex items-start justify-between gap-2">
            <div>
              <a
                href={`https://boardgamegeek.com/boardgame/${result.bggId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-gray-900 hover:text-primary-800"
              >
                {result.title}
              </a>
              {result.yearPublished && (
                <span className="ml-1.5 text-sm text-gray-400">({result.yearPublished})</span>
              )}
              <span className="ml-2 text-xs text-gray-400">#{result.bggId}</span>
              <SimilarityBadge value={result.imageSimilarity} />
            </div>
          </div>
          {result.descriptionSnippet && (
            <p className="mt-1 line-clamp-2 text-xs text-gray-500">{result.descriptionSnippet}</p>
          )}
          {result.existingGameId && (
            <p className="mt-1 text-xs font-medium text-amber-700">
              Jau priskirtas:{" "}
              <a
                href={`/admin/games/${result.existingGameId}`}
                className="underline"
                onClick={onClose}
              >
                {result.existingGameTitle}
              </a>
            </p>
          )}
        </div>
        <div className="mt-2">
          <button
            onClick={onSelect}
            disabled={isPending}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50 ${
              result.existingGameId
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-primary-800 hover:bg-primary-700"
            }`}
          >
            {result.existingGameId ? "Sujungti" : "Priskirti"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InternalResultCard({
  result,
  onSelect,
  isPending,
}: {
  result: InternalSearchResult;
  onSelect: () => void;
  isPending: boolean;
}) {
  return (
    <div className="flex gap-4 rounded-lg border border-gray-200 bg-white p-3">
      <GameThumb url={result.primaryImageUrl || result.thumbnailUrl} alt={result.title} />
      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <div>
          <div className="flex items-start gap-2">
            <div>
              <a
                href={`/admin/games/${result.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-gray-900 hover:text-primary-800"
              >
                {result.title}
              </a>
              {result.yearPublished && (
                <span className="ml-1.5 text-sm text-gray-400">({result.yearPublished})</span>
              )}
              {result.bggId && (
                <span className="ml-2 text-xs text-gray-400">BGG #{result.bggId}</span>
              )}
              <SimilarityBadge value={result.imageSimilarity} />
            </div>
          </div>
          {result.descriptionSnippet && (
            <p className="mt-1 line-clamp-2 text-xs text-gray-500">{result.descriptionSnippet}</p>
          )}
          <div className="mt-1 flex gap-2 text-xs text-gray-400">
            <span>{result.activeListingsCount} skelbimai</span>
            <span>{result.approvalStatus}</span>
          </div>
        </div>
        <div className="mt-2">
          <button
            onClick={onSelect}
            disabled={isPending}
            className="rounded-lg bg-primary-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            Sujungti
          </button>
        </div>
      </div>
    </div>
  );
}
